<script>
  import { LoaderCircle, Plus, Search, UserMinus } from 'lucide-svelte';
  import { avatarContentUrl } from '$lib/avatar';

  export let isGroup = false;
  export let isCurrentUserOwner = false;
  export let members = [];
  export let memberCount = 0;
  export let memberKeyword = '';
  export let memberSuggestions = [];
  export let memberSearchQuery = '';
  export let loadingMemberSearch = false;
  export let memberSearchError = '';
  export let memberActionError = '';
  export let addingMemberId = null;
  export let busyMemberId = null;
  export let getInitial = (value = '') => String(value || '?').trim().charAt(0).toUpperCase() || '?';
  export let getMemberSubtitle = () => '';
  export let isOwnerMember = () => false;
  export let onKeywordChange = () => {};
  export let onAddMember = () => {};
  export let onTransferOwner = () => {};
  export let onRemoveMember = () => {};
</script>

<div class="space-y-3 px-4 pb-4">
  {#if isGroup}
    <div class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
      <div class="flex items-center justify-between gap-3">
        <div class="min-w-0">
          <p class="text-[13px] font-semibold text-slate-950">Thành viên nhóm</p>
          <p class="text-[11px] text-slate-500">{memberCount} thành viên</p>
        </div>
        {#if isCurrentUserOwner}
          <span class="rounded-full border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-600">
            Trưởng nhóm
          </span>
        {/if}
      </div>

      {#if isCurrentUserOwner}
        <div class="relative mt-3">
          <Search size={14} class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={memberKeyword}
            oninput={onKeywordChange}
            type="search"
            autocomplete="off"
            placeholder="Tìm thành viên để thêm"
            class="h-9 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-[12px] text-slate-900 outline-none placeholder:text-[12px] placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
        </div>

        {#if loadingMemberSearch}
          <div class="mt-2 flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-[11px] text-slate-500">
            <LoaderCircle size={13} class="animate-spin text-blue-600" />
            Đang tìm thành viên...
          </div>
        {/if}

        {#if memberSearchError}
          <p class="mt-2 rounded-lg border border-red-100 bg-white px-3 py-2 text-[11px] text-red-600">{memberSearchError}</p>
        {/if}

        {#if memberSearchQuery && memberSuggestions.length}
          <div class="mt-2 max-h-56 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1">
            {#each memberSuggestions as user (user.id)}
              <button
                type="button"
                class="flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-2 text-left transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={!!addingMemberId || !!busyMemberId}
                onclick={() => onAddMember(user)}>
                <span class="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600">
                  {#if avatarContentUrl(user.avatar)}
                    <img src={avatarContentUrl(user.avatar)} alt={user.name} class="h-full w-full object-cover" loading="lazy" decoding="async" />
                  {:else}
                    {getInitial(user.name)}
                  {/if}
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-[12px] font-medium text-slate-900">{user.name || 'Người dùng'}</span>
                  <span class="block truncate text-[11px] text-slate-500">{user.email || user.phone || 'User hệ thống'}</span>
                </span>
                <span class="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-blue-600 text-white">
                  {#if addingMemberId === user.id}
                    <LoaderCircle size={13} class="animate-spin" />
                  {:else}
                    <Plus size={14} />
                  {/if}
                </span>
              </button>
            {/each}
          </div>
        {:else if memberSearchQuery && !loadingMemberSearch && !memberSearchError}
          <p class="mt-2 rounded-lg bg-white px-3 py-3 text-center text-[11px] text-slate-500">Không có thành viên phù hợp.</p>
        {/if}
      {/if}

      {#if memberActionError}
        <p class="mt-3 rounded-lg border border-red-100 bg-white px-3 py-2 text-[11px] text-red-600">{memberActionError}</p>
      {/if}
    </div>
  {/if}

  <div class="space-y-2">
    {#each members as member (member.id)}
      <div class="rounded-xl border border-slate-200 bg-white px-3 py-3">
        <div class="flex min-w-0 items-start gap-3">
        <div class="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600">
          {#if avatarContentUrl(member.avatar)}
            <img src={avatarContentUrl(member.avatar)} alt={member.name} class="h-full w-full object-cover" loading="lazy" decoding="async" />
          {:else}
            {getInitial(member.name)}
          {/if}
        </div>

          <div class="min-w-0 flex-1">
            <div class="flex min-w-0 flex-wrap items-center gap-2">
              <p class="min-w-0 truncate text-[13px] font-medium text-slate-900">{member.name || 'Thành viên'}</p>
            {#if isGroup && isOwnerMember(member)}
              <span class="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                Trưởng nhóm
              </span>
            {/if}
            </div>
            <p class="mt-0.5 text-[11px] leading-5 text-slate-500">{getMemberSubtitle(member)}</p>
          </div>
        </div>

        {#if isGroup && isCurrentUserOwner && !isOwnerMember(member)}
          <div class="mt-3 flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
            <button
              type="button"
              class="rounded-md bg-slate-100 px-2.5 py-1.5 text-[11px] font-medium text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!!busyMemberId || !!addingMemberId}
              onclick={() => onTransferOwner(member)}>
              Trao quyền
            </button>
            <button
              type="button"
              class="grid h-8 w-8 place-items-center rounded-md bg-red-50 text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
              title="Mời rời nhóm"
              aria-label="Mời rời nhóm"
              disabled={!!busyMemberId || !!addingMemberId}
              onclick={() => onRemoveMember(member)}>
              {#if busyMemberId === member.id}
                <LoaderCircle size={13} class="animate-spin" />
              {:else}
                <UserMinus size={14} />
              {/if}
            </button>
          </div>
        {/if}
      </div>
    {:else}
      <div class="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-5 text-center text-[11px] text-slate-500">
        Chưa có dữ liệu thành viên.
      </div>
    {/each}
  </div>
</div>
